import { Injectable } from "@angular/core";
import { getKey } from "pax-ui/core";
import { InvalidOperationException } from "src/app/shared/exceptions/exceptions";
import {
  CustomQueryParamsHandling,
  Page,
  QueryStringParameterKey,
} from "src/app/shared/routes/shared/shared-route.constant";
import { ApplicationFeatureState } from "src/app/shared/store/application/application.state";
import { NotificationMessagePayload } from "src/app/shared/store/notification/notification.actions";
import {
  Code,
  Severity,
} from "src/app/shared/store/notification/notification.state";

import { QuoteFeatureState } from "src/app/shared/store/quote/term-life-insurance/quote.state";
import { ProductService } from "../../../product.service";
import { UrlService } from "../../../url.service";
import { NotificationErrorService } from "../../core/notification-error-whitelist.service";

@Injectable({ providedIn: "root" })
export class CreateApplicationFailedNotificationService {
  constructor(
    private readonly _urlService: UrlService,
    private readonly _productService: ProductService,
  ) {}

  tryCreateMissingUnderwritingQuestionNotificationMessage(
    currentApplicationFeatureState: ApplicationFeatureState,
    currentQuoteFeatureState: QuoteFeatureState,
  ) {
    const hasApplicationCreationFailedDueToMissingUnderwritingQuestions =
      NotificationErrorService.hasApplicationCreationFailedDueToMissingUnderwritingQuestions(
        currentApplicationFeatureState.error,
      );

    if (hasApplicationCreationFailedDueToMissingUnderwritingQuestions) {
      if (!currentQuoteFeatureState.quoteId) {
        throw new InvalidOperationException(
          `When a reset happens the '${getKey<QuoteFeatureState>(
            "quoteId",
          )}' should never be undefined`,
        );
      }

      const notification: NotificationMessagePayload = {
        code: Code.HAS_UNDERWRITING_QUESTIONS_PASSED_BEEN_RESET,
        severity: Severity.WARNING,
        markdown: this.getUnderwritingQuestionResetAlertContent(
          currentQuoteFeatureState.quoteId,
        ),
        deleteAfterNextNavigation: true,
      };

      return notification;
    }

    return undefined;
  }

  private getUnderwritingQuestionResetAlertContent(quoteId: string) {
    const product = this._productService.getProduct();

    // Unfortunately we can not simply get the 'quoteId' from the current url as there might be a new 'quoteId' being
    // returned by the backend (the replacement of the quoteId with the new one in the url has not happened yet).
    // This happens if the we would like to change the 'insuredSum' on the summary page on an already locked quote.
    const urlSearchParams = new URLSearchParams({
      [QueryStringParameterKey.QUOTE_ID]: quoteId,
    });

    const underwritingQuestionPageUrl =
      this._urlService.createAbsoluteUrlByPage(
        product,
        Page.QUOTE_UNDERWRITING_QUESTIONS,
        CustomQueryParamsHandling.MERGE,
        urlSearchParams,
      );

    return $localize`Die Änderungen von Ihnen könnten einen Einfluss auf die Gesundheitsfragen haben und wir möchten sie deshalb bitten, die Fragen nochmals auszufüllen.<br>[Gesundheitsfragen](${underwritingQuestionPageUrl}:URL:)`;
  }
}
